import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import Layout from "../components/layout";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { Form, Input, Button, Select, Rate, Typography, Divider, Avatar } from "antd";
import toast from "react-hot-toast";
import {
  StarOutlined, MessageOutlined,
  SendOutlined, CalendarOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("review-styles")) return;
  const el = document.createElement("style");
  el.id = "review-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .rv-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }

    .rv-card {
      background: ${WHITE};
      border-radius: 28px;
      overflow: hidden;
      box-shadow: 0 24px 64px rgba(0,0,0,.09);
      display: flex; flex-wrap: wrap;
    }

    .rv-left {
      background: linear-gradient(160deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      padding: 48px 36px;
      flex: 0 0 260px;
      display: flex; flex-direction: column; justify-content: center;
      position: relative; overflow: hidden;
    }
    .rv-left::before {
      content: '';
      position: absolute; top: -60px; right: -60px;
      width: 220px; height: 220px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(28px); pointer-events: none;
    }
    .rv-left::after {
      content: '';
      position: absolute; bottom: -40px; left: -40px;
      width: 180px; height: 180px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(24px); pointer-events: none;
    }

    .rv-right { flex: 1 1 340px; padding: 48px 44px; }

    .rv-right .ant-form-item-label > label {
      font-weight: 600 !important;
      color: ${SLATE_MID} !important;
      font-size: 13px !important;
      font-family: 'DM Sans', sans-serif !important;
    }

    .rv-right .ant-input,
    .rv-right .ant-select-selector,
    .rv-right .ant-input-textarea textarea {
      border-radius: 12px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-family: 'DM Sans', sans-serif !important;
      font-size: 14px !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .rv-right .ant-select-selector { height: auto !important; padding: 6px 12px !important; }
    .rv-right .ant-input:focus,
    .rv-right .ant-select-focused .ant-select-selector,
    .rv-right .ant-input-textarea textarea:focus {
      border-color: ${TEAL} !important;
      box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    .rv-right .ant-rate-star { font-size: 28px !important; }

    .rv-rate-wrap {
      padding: 14px 16px; border-radius: 14px;
      background: #fffbeb; border: 1.5px solid #fde68a;
      display: inline-flex; align-items: center; gap: 8px;
    }

    .rv-submit-btn {
      height: 52px !important; width: 100%;
      border-radius: 14px !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important;
      font-size: 15px !important; font-weight: 700 !important;
      font-family: 'Sora', sans-serif !important;
      color: ${WHITE} !important;
      letter-spacing: .3px;
      transition: transform .2s, box-shadow .2s !important;
    }
    .rv-submit-btn:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 12px 32px ${TEAL}55 !important;
    }

    .rv-step {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,.08);
    }
    .rv-step:last-child { border-bottom: none; }
    .rv-step-num {
      width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
      background: rgba(20,184,166,.2); color: ${TEAL};
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700; font-family: 'Sora', sans-serif;
      border: 1px solid rgba(20,184,166,.4);
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .rv-fade   { animation: fadeUp .5s ease both; }
    .rv-fade-1 { animation-delay: .05s; }
    .rv-fade-2 { animation-delay: .15s; }

    @media (max-width: 680px) {
      .rv-left  { flex: 0 0 100%; padding: 36px 24px; }
      .rv-right { padding: 36px 24px; }
    }
  `;
  document.head.appendChild(el);
};

const STAR_DESC = ["Terrible", "Bad", "Average", "Good", "Excellent"];

function SubmitReview() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [appointments, setAppointments] = useState([]);
  const [ratingValue, setRatingValue] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(showLoading());
        const res = await axios.get("/api/user/get-appointments-by-user-id", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        dispatch(hideLoading());
        setAppointments(res.data.data || []);
      } catch (error) {
        toast.error("Failed to fetch appointments");
        dispatch(hideLoading());
      }
    };
    fetchData();
  }, [dispatch]);

  const submitReview = async (values) => {
    try {
      dispatch(showLoading());
      const res = await axios.post("/api/bookAppointment/submit-review", values, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) {
        toast.success(res.data.message);
        form.resetFields();
        setRatingValue(0);
      }
    } catch {
      toast.error("Something went wrong");
      dispatch(hideLoading());
    }
  };

  const activeRating = hoverRating || ratingValue;

  return (
    <Layout>
      <div className="rv-root">
        <div style={{ maxWidth: 1000, margin: "40px auto", padding: "0 20px 60px" }}>

          {/* Page header */}
          <div className="rv-fade" style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{
              display: "inline-block", padding: "4px 14px", borderRadius: 20,
              background: TEAL_LIGHT, color: EMERALD,
              fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
              marginBottom: 12, fontFamily: "'Sora', sans-serif"
            }}>
              <StarOutlined style={{ marginRight: 5 }} />Patient Feedback
            </div>
            <Title level={2} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 800 }}>
              Leave a Review
            </Title>
            <Text style={{ color: MUTED, fontSize: 14 }}>
              Your feedback helps other patients find trusted care
            </Text>
          </div>

          <div className="rv-card rv-fade rv-fade-1">

            {/* ── Left panel ── */}
            <div className="rv-left">
              <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{
                  width: 60, height: 60, borderRadius: 18, marginBottom: 24,
                  background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, color: WHITE, boxShadow: `0 8px 24px ${TEAL}55`,
                }}>
                  <StarOutlined />
                </div>

                <Title level={4} style={{ color: WHITE, fontFamily: "'Sora', sans-serif", margin: "0 0 6px" }}>
                  How it works
                </Title>
                <Text style={{ color: MUTED, fontSize: 13, display: "block", marginBottom: 24 }}>
                  3 simple steps to share your experience
                </Text>

                {[
                  { n: 1, title: "Select Appointment", sub: "Choose the visit you're reviewing" },
                  { n: 2, title: "Rate the Doctor",    sub: "1 to 5 stars based on your experience" },
                  { n: 3, title: "Write a Comment",    sub: "Share details to help others" },
                ].map(s => (
                  <div key={s.n} className="rv-step">
                    <div className="rv-step-num">{s.n}</div>
                    <div>
                      <Text style={{ color: WHITE, fontSize: 13, fontWeight: 600, display: "block" }}>{s.title}</Text>
                      <Text style={{ color: MUTED, fontSize: 11 }}>{s.sub}</Text>
                    </div>
                  </div>
                ))}

                <Divider style={{ borderColor: "rgba(255,255,255,.1)", margin: "24px 0 16px" }} />
                <Text style={{ color: MUTED, fontSize: 12 }}>
                  Reviews are visible to all patients looking for trusted care.
                </Text>
              </div>
            </div>

            {/* ── Right form panel ── */}
            <div className="rv-right rv-fade rv-fade-2">
              <Title level={4} style={{ margin: "0 0 4px", fontFamily: "'Sora', sans-serif", color: SLATE, fontWeight: 700 }}>
                Share Your Experience
              </Title>
              <Text style={{ color: MUTED, fontSize: 13 }}>All fields are required</Text>

              <Divider style={{ margin: "20px 0" }} />

              <Form form={form} layout="vertical" onFinish={submitReview}>

                {/* Appointment selector */}
                <Form.Item
                  name="appointmentId"
                  label={<span style={{ display: "flex", alignItems: "center", gap: 6 }}><CalendarOutlined style={{ color: EMERALD }} />Appointment</span>}
                  rules={[{ required: true, message: "Please select an appointment" }]}
                >
                  <Select
                    placeholder="Select the doctor you visited"
                    notFoundContent={<div style={{ textAlign: "center", padding: "12px 0", color: MUTED }}>No appointments found</div>}
                    optionLabelProp="label"
                  >
                    {appointments.map((appt) => (
                      <Option
                        key={appt._id}
                        value={appt._id}
                        label={`Dr. ${appt.doctorInfo?.firstName} ${appt.doctorInfo?.lastName}`}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
                          <Avatar size={32} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 13, flexShrink: 0 }}>
                            {appt.doctorInfo?.firstName?.[0]}
                          </Avatar>
                          <div>
                            <Text strong style={{ fontSize: 13, display: "block" }}>
                              Dr. {appt.doctorInfo?.firstName} {appt.doctorInfo?.lastName}
                            </Text>
                            <Text style={{ fontSize: 11, color: MUTED }}>{appt.doctorInfo?.service || "General"}</Text>
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Rating */}
                <Form.Item
                  name="rating"
                  label={<span style={{ display: "flex", alignItems: "center", gap: 6 }}><StarOutlined style={{ color: EMERALD }} />Rating</span>}
                  rules={[{ required: true, message: "Please provide a rating" }]}
                >
                  <div className="rv-rate-wrap">
                    <Rate
                      onChange={(val) => { setRatingValue(val); form.setFieldsValue({ rating: val }); }}
                      onHoverChange={setHoverRating}
                      value={ratingValue}
                    />
                    {activeRating > 0 && (
                      <Text style={{ color: "#b45309", fontSize: 13, fontWeight: 600, marginLeft: 8 }}>
                        {STAR_DESC[activeRating - 1]}
                      </Text>
                    )}
                  </div>
                </Form.Item>

                {/* Comment */}
                <Form.Item
                  name="comment"
                  label={<span style={{ display: "flex", alignItems: "center", gap: 6 }}><MessageOutlined style={{ color: EMERALD }} />Comment</span>}
                  rules={[{ required: true, message: "Please write a comment" }]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="Describe your experience — what went well, what could be improved..."
                    maxLength={500}
                    showCount
                    style={{ resize: "none" }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                  <Button htmlType="submit" className="rv-submit-btn" icon={<SendOutlined />}>
                    Submit Review
                  </Button>
                </Form.Item>
              </Form>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SubmitReview;